import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Copy, Check, Trash2, CheckCircle2 } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { PageSkeleton } from '@/components/shared/page-skeleton'
import { toast } from 'sonner'
import { useSettings, QUERY_KEYS } from '@/hooks'
import { api, type MerchantSettings } from '@/lib/api'
import { SectionCard } from '@/components/shared/section-card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { EventsTimeline } from '@/components/settings/events-timeline'
import { ChonkyButton } from '@/components/shared/chonky-button'

export const Route = createFileRoute('/_app/settings/integrations/yampi')({
  component: YampiSettingsPage,
})

function YampiSettingsPage() {
  const { data: settings, isLoading } = useSettings()

  if (isLoading) {
    return <PageSkeleton sections={2} />
  }

  if (!settings) return null

  return (
    <div className="space-y-6">
      <PageHeader
        size="sm"
        title="Yampi"
        description="Crie cupons e rastreie pedidos do seu e-commerce."
        backHref="/settings/integrations"
      />

      <YampiConfigSection settings={settings} />

      {settings.hasYampiToken && (
        <>
          <Separator />

          <div className="space-y-4">
            <div>
              <h4 className="text-base font-medium">Últimos eventos da Yampi</h4>
              <p className="text-sm text-muted-foreground">Eventos recebidos desta integração.</p>
            </div>
            <EventsTimeline
              filter={{ platform: 'yampi', direction: 'in' }}
              limit={10}
              showFilters={false}
            />
          </div>
        </>
      )}
    </div>
  )
}

function YampiConfigSection({ settings }: { settings: MerchantSettings }) {
  const queryClient = useQueryClient()
  const [yampiToken, setYampiToken] = useState('')
  const [yampiSecret, setYampiSecret] = useState('')
  const [yampiAlias, setYampiAlias] = useState(settings.yampiAlias || '')
  const [webhookSecret, setWebhookSecret] = useState(settings.webhookSecret || '')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const updateIntegrations = useMutation({
    mutationFn: (data: {
      yampiApiToken?: string
      yampiSecret?: string
      yampiAlias?: string
      webhookSecret?: string
    }) => api.settings.updateIntegrations(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings })
      setYampiToken('')
      setYampiSecret('')
      toast.success('Configuração salva!')
    },
    onError: () => {
      toast.error('Erro ao salvar configuração')
    },
  })

  const removeIntegration = useMutation({
    mutationFn: () => api.settings.removeIntegration('yampi'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.settings })
      toast.success('Integração removida')
    },
  })

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <SectionCard title="Configuração" description="Conecte sua loja Yampi ao Rafobase.">
      <div className="space-y-6">
        {/* Webhook Config */}
        <div className="space-y-4 p-4 rounded-md bg-muted/30 border">
          <h4 className="text-sm font-medium">Webhook (receber pedidos)</h4>

          <div className="space-y-2">
            <Label className="text-xs">URL do Webhook</Label>
            <div className="flex gap-2">
              <Input value={settings.yampiWebhookUrl} readOnly className="font-mono text-xs h-8" />
              <ChonkyButton
                variant="secondary"
                size="icon"
                onClick={() => copyToClipboard(settings.yampiWebhookUrl, 'yampi-webhook')}
              >
                {copiedId === 'yampi-webhook' ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </ChonkyButton>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Eventos: <code>order.paid</code>, <code>order.status.updated</code>
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Token de Validação</Label>
            <div className="flex gap-2">
              <Input
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                type="password"
                className="h-8 text-xs"
                placeholder="Cole o token da Yampi"
              />
              <ChonkyButton
                size="sm"
                disabled={webhookSecret === settings.webhookSecret}
                loading={updateIntegrations.isPending}
                onClick={() => updateIntegrations.mutate({ webhookSecret })}
              >
                Salvar
              </ChonkyButton>
            </div>
          </div>
        </div>

        {/* Alias */}
        <div className="space-y-2">
          <Label>Alias da Loja</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Ex: minha-loja"
              value={yampiAlias}
              onChange={(e) => setYampiAlias(e.target.value)}
            />
            <ChonkyButton
              disabled={yampiAlias === settings.yampiAlias}
              loading={updateIntegrations.isPending}
              onClick={() => updateIntegrations.mutate({ yampiAlias })}
            >
              Salvar
            </ChonkyButton>
          </div>
          <p className="text-xs text-muted-foreground">
            O alias é parte da URL da sua loja: yampi.com.br/
            <strong>{yampiAlias || 'alias'}</strong>
          </p>
        </div>

        {/* API Credentials */}
        <div className="space-y-2">
          <Label>Credenciais da API</Label>
          {settings.hasYampiToken ? (
            <div className="flex items-center justify-between p-3 border rounded-md bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  Conectado
                </span>
              </div>
              <ChonkyButton
                variant="destructive"
                size="sm"
                onClick={() => removeIntegration.mutate()}
                loading={removeIntegration.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Desconectar
              </ChonkyButton>
            </div>
          ) : (
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="User Token..."
                value={yampiToken}
                onChange={(e) => setYampiToken(e.target.value)}
              />
              <Input
                type="password"
                placeholder="User Secret..."
                value={yampiSecret}
                onChange={(e) => setYampiSecret(e.target.value)}
              />
              <ChonkyButton
                className="w-full"
                onClick={() =>
                  updateIntegrations.mutate({
                    yampiApiToken: yampiToken,
                    yampiSecret: yampiSecret,
                    yampiAlias,
                  })
                }
                disabled={!yampiToken || !yampiSecret || !yampiAlias}
                loading={updateIntegrations.isPending}
              >
                Conectar
              </ChonkyButton>
              <p className="text-xs text-muted-foreground">
                Encontre em Credenciais de API no painel da Yampi.
              </p>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  )
}
